import { format } from "date-fns";
import { DdqAuditEventType } from "~/graphql/supergraphEnums";
import { formatShortFormDate, toCalendarDate } from "~/utils/datetime";
import type { GetDdqProdOpsWorkflowLogQuery } from "~/graphql/supergraphTypes";

export type DdqProdOpsLogEntry =
  GetDdqProdOpsWorkflowLogQuery["ddqProdOpsWorkflowLog"]["entries"][number];

const namesOf = (users: DdqProdOpsLogEntry["namedUsers"]): string =>
  users.map((user) => user.fullName ?? "Unnamed user").join(", ");

const asString = (value: unknown): string | null => (typeof value === "string" ? value : null);

const asNumber = (value: unknown): number | null => (typeof value === "number" ? value : null);

// Covers every DdqAuditEventType; PROD_OPS_* is this workflow's own activity, the rest
// (answer/KB events) belong to other DDQ surfaces but can still show up in a shared enum.
export const formatLogMessage = (entry: DdqProdOpsLogEntry): string => {
  const detail = (entry.detail ?? {}) as Record<string, unknown>;

  switch (entry.eventType) {
    case DdqAuditEventType.ProdOpsWorkflowCreated:
      return "Request created";
    case DdqAuditEventType.ProdOpsWorkflowHeld:
      // The comment text itself shows up as a paired PROD_OPS_COMMENT_ADDED entry.
      return "Placed the request on hold";
    case DdqAuditEventType.ProdOpsWorkflowRestarted:
      return "Request restarted";
    case DdqAuditEventType.ProdOpsWorkflowCancelled:
      return "Request cancelled";
    case DdqAuditEventType.ProdOpsTaskAssigned: {
      const assignee = entry.namedUsers[0]?.fullName ?? "Unnamed user";
      return `Assigned to ${assignee}`;
    }
    case DdqAuditEventType.ProdOpsTaskStarted:
      return "Task started";
    case DdqAuditEventType.ProdOpsCommentAdded:
      return asString(detail.comment) ?? "Left a comment";
    case DdqAuditEventType.ProdOpsExtractionStarted: {
      const attempt = asNumber(detail.attempt);
      return attempt ? `Extraction started (attempt ${attempt})` : "Extraction started";
    }
    // Started-only, matching extraction's own precedent — no attempt count on this one.
    case DdqAuditEventType.ProdOpsGenerationStarted:
      return "Generation started";
    case DdqAuditEventType.ProdOpsReadyForReview:
      return "Marked ready for review";
    case DdqAuditEventType.ProdOpsApprovedForGp:
      return "Approved for GP";
    case DdqAuditEventType.ProdOpsReturnedForEdits:
      // The comment text itself shows up as a paired PROD_OPS_COMMENT_ADDED entry.
      return "Returned for edits";
    case DdqAuditEventType.ProdOpsOwnerChanged:
      return entry.namedUsers.length > 0
        ? `Owners changed to ${namesOf(entry.namedUsers)}`
        : "Owners changed to no owners";
    case DdqAuditEventType.ProdOpsDueDateChanged: {
      // before/after are full UTC-midnight datetimes, not date-only strings -- read via
      // toCalendarDate rather than formatShortFormDate, which uses local Date fields and
      // would show the previous day in any timezone ahead of UTC.
      const before = asString(detail.before);
      const after = asString(detail.after);
      const afterLabel = after ? format(toCalendarDate(after), "MMM d, yyyy") : "-";
      return before
        ? `Due date changed from ${format(toCalendarDate(before), "MMM d, yyyy")} to ${afterLabel}`
        : `Due date set to ${afterLabel}`;
    }
    case DdqAuditEventType.ProdOpsSubmitByDateChanged: {
      const before = asString(detail.before);
      const after = asString(detail.after);
      const afterLabel = after ? formatShortFormDate(after) : "-";
      return before
        ? `Submit-by date changed from ${formatShortFormDate(before)} to ${afterLabel}`
        : `Submit-by date set to ${afterLabel}`;
    }
    case DdqAuditEventType.AnswerUpdated:
      return "Answer updated";
    case DdqAuditEventType.KbDocumentAiToggled:
      return "Knowledge base document AI toggled";
    default:
      return "Activity logged";
  }
};
